import React from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import type { MetaBidStrategy } from '@/services/api/metaAds';

export type MetaBudgetMode = 'daily' | 'lifetime';

interface MetaBudgetStepProps {
  budgetMode: MetaBudgetMode;
  budgetAmount: string;     // dollars as string for controlled input
  bidStrategy: MetaBidStrategy;
  onChangeMode: (mode: MetaBudgetMode) => void;
  onChangeAmount: (amount: string) => void;
  onChangeBidStrategy: (strategy: MetaBidStrategy) => void;
  errors?: {
    amount?: string;
  };
}

const BID_STRATEGY_OPTIONS: Array<{
  value: MetaBidStrategy;
  label: string;
  description: string;
}> = [
  {
    value: 'LOWEST_COST_WITHOUT_CAP',
    label: 'Lowest cost',
    description:
      'Spend the budget to get the most results. Meta optimizes freely without a cap. (Default — best for most campaigns.)',
  },
  {
    value: 'LOWEST_COST_WITH_BID_CAP',
    label: 'Lowest cost with bid cap',
    description:
      'Meta optimizes for lowest cost but never bids above your cap. The cap value is set per ad set.',
  },
  {
    value: 'COST_CAP',
    label: 'Cost cap',
    description:
      'Meta aims to keep your average cost per result at or below the cap. The cap value is set per ad set.',
  },
  {
    value: 'LOWEST_COST_WITH_MIN_ROAS',
    label: 'Minimum ROAS',
    description:
      'Meta tries to keep your return on ad spend at or above the floor. The floor value is set per ad set.',
  },
];

/**
 * Step 3 — budget mode (daily XOR lifetime) + amount in USD.
 *
 * The XOR is enforced at both the wizard's local state and the backend
 * model_validator. Changing modes clears the inactive field's value so the
 * payload never carries both.
 *
 * Currency is dollars (major units) at the UI + API-request boundary; the
 * backend converts to cents at the service boundary. See route source for
 * `_dollars_to_cents` rationale.
 */
export const MetaBudgetStep: React.FC<MetaBudgetStepProps> = ({
  budgetMode,
  budgetAmount,
  bidStrategy,
  onChangeMode,
  onChangeAmount,
  onChangeBidStrategy,
  errors,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Budget</h3>
        <p className="text-sm text-gray-600">
          Choose how to spread spend across the campaign. Lifetime budgets
          require a stop date on the next step.
        </p>
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">
          Budget mode
        </p>
        <div role="radiogroup" aria-label="Budget mode" className="flex gap-2">
          {(['daily', 'lifetime'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={budgetMode === mode}
              onClick={() => onChangeMode(mode)}
              className={cn(
                'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                budgetMode === mode
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
              )}
            >
              {mode === 'daily' ? 'Daily budget' : 'Lifetime budget'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label
          htmlFor="meta-budget-amount"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Amount (USD)
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            $
          </span>
          <Input
            id="meta-budget-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={budgetAmount}
            onChange={(e) => onChangeAmount(e.target.value)}
            placeholder={budgetMode === 'daily' ? '25.00' : '500.00'}
            className="pl-7"
            aria-invalid={Boolean(errors?.amount)}
            aria-describedby={errors?.amount ? 'meta-budget-amount-error' : 'meta-budget-amount-hint'}
          />
        </div>
        <p
          id="meta-budget-amount-hint"
          className="text-xs text-gray-500 mt-1"
        >
          Meta enforces per-objective minimums. If Meta rejects the campaign
          for being below the floor, the message will tell you the minimum.
        </p>
        {errors?.amount && (
          <p
            id="meta-budget-amount-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.amount}
          </p>
        )}
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">
          Bid strategy
        </p>
        <p className="text-xs text-gray-500 mb-2">
          How Meta should optimize the spend. Cap and floor values for the
          three constrained strategies are set per ad set later.
        </p>
        <div role="radiogroup" aria-label="Bid strategy">
          {BID_STRATEGY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={bidStrategy === opt.value}
              onClick={() => onChangeBidStrategy(opt.value)}
              className={cn(
                'w-full text-left p-4 border-2 rounded-lg mb-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                bidStrategy === opt.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50 bg-white',
              )}
            >
              <div className="text-sm font-medium text-gray-900">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-1">{opt.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
